import { useEffect, useState } from 'react';
import { PageHeader } from '@platform/ui-kit';
import { api, type AirFitmentCriteria } from '../../../api';
import { PlatformCriteriaEditor } from '../PlatformCriteriaEditor';
import './PlatformFitmentCriteria.css';

/**
 * Strategic Intelligence → Platform Fitment Criteria.
 *
 * Cloned from `views/platforms/fit-criteria.ejs` — markup, class names, copy and every
 * CSS value, with the stylesheet carrying the line each rule came from.
 *
 * ── THIS WAS BUILT RATHER THAN CLONED, AND WAS CORRECTED ON 2026-08-17 ─────────────────
 *
 * The first version used MUI `Tabs`, `Slider`, `Alert` and `Tooltip`. It had the three tab
 * LABELS and nothing else the source has: no lock bar with its two states, no underlined
 * tab strip, no bordered panel, no numbered readiness factors, no two-block weighting
 * readout, no formula line. It was a different screen that happened to hold the same data.
 *
 * ── THE STRUCTURE, WHICH IS THE POINT ──────────────────────────────────────────────────
 *
 * Three tabs, ONE lock across all of them. The lock is not a UI nicety: locked makes these
 * the fixed standard every AI Platform analysis runs against, and AI Platform Fit refuses
 * to analyse until they are locked, because an analysis run against a rubric that can
 * still move is not comparable with the ones before it.
 *
 * The tab also lives in the URL hash (`#capability` / `#readiness` / `#weighting`), as the
 * source does it, so a tab can be linked to and survives a reload.
 */

type TabKey = 'cap' | 'rdy' | 'wt';

const HASH: Record<TabKey, string> = {
  cap: 'capability',
  rdy: 'readiness',
  wt: 'weighting',
};

function tabFromHash(): TabKey {
  const h = (window.location.hash || '').replace('#', '');
  if (h === 'readiness') return 'rdy';
  if (h === 'weighting') return 'wt';
  return 'cap';
}

export function PlatformFitmentCriteria() {
  const [data, setData] = useState<AirFitmentCriteria | null>(null);
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const load = () => {
    setLoading(true);
    api.aiReadiness
      .getFitmentCriteria()
      .then(setData)
      .catch((e: Error) => setError(e?.message || 'Could not load the criteria.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pickTab = (t: TabKey) => {
    setTab(t);
    window.history.replaceState(null, '', `#${HASH[t]}`);
  };

  if (loading) return <p className="fc-note">Loading…</p>;
  if (!data) return <p className="fc-warn">{error || 'Unavailable.'}</p>;

  const locked = data.locked;
  const wtCap = data.weightCapability;
  const wtRdy = 100 - wtCap;

  const save = async () => {
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      /* Only the profile and the weighting. The criterion WEIGHTS belong to the Platform
         Capabilities tab and are saved by its own editor — sending them here wrote
         `platform_criteria` ids into a column keyed to `dimensions` and broke saving the
         client profile with a foreign-key violation. */
      await api.aiReadiness.saveFitmentCriteria({
        clientProfile: data.clientProfile,
        weightCapability: data.weightCapability,
      });
      setNotice('Fitment criteria saved.');
    } catch (e) {
      setError((e as Error).message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const toggleLock = async () => {
    /* The source confirms before UNLOCKING when analyses already exist, and says how many
       — because unlocking is what lets the standard move underneath them. */
    if (locked && data.analyzedCount > 0) {
      const ok = window.confirm(
        `AI Platform analysis already exists for ${data.analyzedCount} opportunities. ` +
          'Unlock to change the criteria anyway? Existing analyses will not update.',
      );
      if (!ok) return;
    }
    try {
      const r = await api.aiReadiness.toggleFitmentLock();
      setData({ ...data, locked: r.locked });
      setNotice(
        r.locked
          ? 'Criteria locked as the standard for all AI Platform analyses.'
          : 'Criteria unlocked for editing.',
      );
    } catch (e) {
      setError((e as Error).message || 'Could not change the lock.');
    }
  };

  return (
    <>
      <PageHeader
        title="Platform Fitment Criteria"
        description="The standard capability criteria, organizational readiness and weighting applied to every AI Platform analysis."
      />

      <div className="fc-wrap" style={{ marginTop: 16 }}>
        {data.analyzedCount > 0 ? (
          <div className="fc-warn">
            ⚠️ AI Platform analysis has already been executed for{' '}
            <strong>{data.analyzedCount}</strong> opportunit
            {data.analyzedCount === 1 ? 'y' : 'ies'}. Changing anything here will{' '}
            <strong>not</strong> update those analyses — you would need to re-run them.
          </div>
        ) : null}

        <div className={`fc-lockbar ${locked ? 'locked' : 'unlocked'}`}>
          <span>
            {locked
              ? '🔒 LOCKED — the fixed standard for all analyses (capability, readiness & weighting).'
              : '🔓 UNLOCKED — editable. Lock to use as the standard.'}
          </span>
          <span className="grow" />
          <button
            type="button"
            className={`fc-btn-small ${locked ? 'orange' : 'green'}`}
            onClick={() => void toggleLock()}
          >
            <i className="material-icons">{locked ? 'lock_open' : 'lock'}</i>
            {locked ? 'Unlock to edit' : 'Lock as standard'}
          </button>
        </div>

        {error ? <div className="fc-warn">{error}</div> : null}
        {notice ? (
          <div className="fc-lockbar locked" style={{ marginBottom: 14 }}>
            <span>{notice}</span>
          </div>
        ) : null}

        <div className="fc-tabs">
          <button
            type="button"
            className={`fctab${tab === 'cap' ? ' active' : ''}`}
            onClick={() => pickTab('cap')}
          >
            <i className="material-icons">tune</i> Platform Capabilities
          </button>
          <button
            type="button"
            className={`fctab${tab === 'rdy' ? ' active' : ''}`}
            onClick={() => pickTab('rdy')}
          >
            <i className="material-icons">groups</i> Organizational Readiness
          </button>
          <button
            type="button"
            className={`fctab${tab === 'wt' ? ' active' : ''}`}
            onClick={() => pickTab('wt')}
          >
            <i className="material-icons">balance</i> Weighting
          </button>
        </div>

        {/*
          TAB 1: Platform Capabilities — the AI Platform Selection Criteria.

          OWNER-DIRECTED, 2026-08-17: "The content from ...ai-platform-selection-criteria
          should replace the content on ...platform-fitment-criteria in tab 'Platform
          Capability'. The label of that tab should be changed to 'Platform Capabilities'."

          What was here: a priority 1-5 per framework DIMENSION — the opportunity assessment
          framework's eight axes. That is the duplication being resolved. The weighted
          criteria list is the purpose-built set for judging a platform, and it is what
          Platform Analysis now scores against.

          Those dimension priorities are still LOADED and still SAVED unchanged by this
          screen — they are what AI Platform Fit's prompt reads as its standard rubric.
          They are simply no longer edited here, because this tab is now about platform
          capabilities rather than opportunity dimensions.
        */}
        {tab === 'cap' ? (
          <div className="fc-panel">
            <PlatformCriteriaEditor disabled={locked} />
          </div>
        ) : null}

        {/* TAB 2: Organizational Readiness (free-text client profile) */}
        {tab === 'rdy' ? (
          <div className="fc-panel">
            <p style={{ fontSize: '13.5px', color: '#455a64', lineHeight: 1.6, margin: '0 0 14px' }}>
              Describe the client&apos;s environment once. Every analysis reads it to score how
              readily the client can adopt each platform, on these three factors (everything else
              is already covered by the 8 capability criteria):
            </p>
            <ol className="rd-factors">
              {(data.factors ?? []).map((f, i) => (
                <li key={f.code}>
                  <span className="num">{i + 1}</span>
                  <span>
                    <b>{f.name}</b> — {f.what}
                  </span>
                </li>
              ))}
            </ol>
            <textarea
              className="rd-ta"
              disabled={locked}
              value={data.clientProfile}
              onChange={(e) => setData({ ...data, clientProfile: e.target.value })}
              placeholder="e.g. Microsoft-centric enterprise: Microsoft 365, Azure and Active Directory across the estate, with a Microsoft Enterprise Agreement in place. Strong in-house .NET and Azure data engineering team; a small data-science group with OpenAI pilot experience. No AWS or GCP footprint or skills. Security requires all AI to run within the Azure tenant."
            />
            <div className="rd-eg">
              Mention: cloud &amp; stack, in-house skills / production experience per vendor, and
              any enterprise agreements or preferred vendors.
            </div>
            {!locked ? (
              <button type="button" className="fc-btn" disabled={saving} onClick={() => void save()}>
                <i className="material-icons">save</i>
                {saving ? 'Saving…' : 'Save client profile'}
              </button>
            ) : (
              <p className="fc-note">Unlock above to edit the profile.</p>
            )}
          </div>
        ) : null}

        {/* TAB 3: Weighting */}
        {tab === 'wt' ? (
          <div className="fc-panel">
            <p style={{ fontSize: '13.5px', color: '#455a64', lineHeight: 1.6, margin: '0 0 8px' }}>
              How the two axes blend into the single <strong>Overall</strong> recommendation shown
              on the Fit and Summary screens.
            </p>
            <div className="wt-readout">
              <div className="s cap">
                <div className="n">{wtCap}%</div>
                <div className="l">Platform capability</div>
              </div>
              <div className="s rdy">
                <div className="n">{wtRdy}%</div>
                <div className="l">Organizational readiness</div>
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              className="wt-slider"
              disabled={locked}
              value={wtCap}
              onChange={(e) => setData({ ...data, weightCapability: Number(e.target.value) })}
            />
            <div className="wt-formula">
              Overall = {wtCap}% × Requirement&nbsp;Fit &nbsp;+&nbsp; {wtRdy}% ×
              Adoption&nbsp;Readiness
            </div>
            {!locked ? (
              <button
                type="button"
                className="fc-btn"
                style={{ marginTop: 16 }}
                disabled={saving}
                onClick={() => void save()}
              >
                <i className="material-icons">save</i>
                {saving ? 'Saving…' : 'Save weighting'}
              </button>
            ) : (
              <p className="fc-note">Unlock above to change the weighting.</p>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
